import htmx from 'htmx.org';
import { isAuthenticated } from '../../Shared/scriptts/session/sessionmain';
import { DefaultExistingParams, Params } from '../../Shared/scriptts/search-params';
import { params, setParams, signinClick } from '../search-main';
import { isEmpty, loadAlertBanner } from '../../Shared/scriptts/utilities';
import { bindToggles, toggleElement } from '../../Shared/scriptts/rootcomponents/toggle';
import { getNormalizedFilterName } from '../search-filters-main';

const editAlertTitle = 'Edit job alert';
const editAlertButtonText = 'Save job alert';
const newAlertTitle = 'Turn on job alert';
const newAlertButtonText = 'Add new job alert';
const defaultEmailFrequency = 'Daily';
const seeAllLink = `<a href="/applicant/dashboard/savedsearches/">See all job alerts</a>.`;

let isSavedSearch = false;

document.addEventListener('DOMContentLoaded', () => {
    document.querySelector<HTMLButtonElement>('#job-alert-signin')?.addEventListener('click', signinClick);
    document.querySelector<HTMLButtonElement>('#job-alert-toggle')?.addEventListener('click', saveSearchToggleClick);
    htmx.on(document.body, 'htmx:afterRequest', jobAlertFormSubmit);
});

function jobAlertFormSubmit(evt: CustomEvent<any>) {
    const detail = evt.detail;
    const triggerElement = detail.elt as HTMLElement;

    if (triggerElement?.id !== 'job-alert-filter-container') {
        return;
    }

    bindToggles();

    const searchMessages = document.querySelector<HTMLElement>('#search-messages');
    const errors = document.querySelector<HTMLFormElement>('#job-alert-form')?.querySelectorAll<HTMLElement>('.input-error-message');
    const errorMessages: string[] = [];

    errors?.forEach(elem => {
        elem.classList.add('hidden');
        if (elem.textContent && elem.textContent.trim() !== '') {
            errorMessages.push(`${elem.textContent.trim()} ${seeAllLink}`);
        }
    });

    if (errorMessages.length > 0) {
        const toggle = document.querySelector<HTMLButtonElement>('#job-alert-toggle');
        if (!toggle) {
            return;
        }
        toggleElement(toggle, true);

        if (!searchMessages) {
            return;
        }

        loadAlertBanner(searchMessages, { alertType: 'Error', message: errorMessages.join(' '), isDismissable: true });
    }
    else {
        isSavedSearch = true;

        if (!searchMessages) {
            return;
        }

        const frequency = document.querySelector<HTMLInputElement>('#job-alert-form input[name="EmailFrequency"]:checked')?.value || defaultEmailFrequency;

        loadAlertBanner(searchMessages, { alertType: 'Success', message: getSuccessMessage(frequency), isDismissable: true });
    }
}

export function displayJobAlertForm(visible: boolean) {
    if (visible === true) {
        document.querySelector<HTMLElement>('#job-alert-filter-container')?.classList.remove('hidden');
        if (isAuthenticated() === true) {
            document.querySelector<HTMLElement>('#job-alert-auth')?.classList.remove('hidden');
            document.querySelector<HTMLElement>('#job-alert-noauth')?.classList.add('hidden');
        }
        else {
            document.querySelector<HTMLElement>('#job-alert-auth')?.classList.add('hidden');
            document.querySelector<HTMLElement>('#job-alert-noauth')?.classList.remove('hidden');
        }
    }
    else {
        document.querySelector<HTMLElement>('#job-alert-filter-container')?.classList.add('hidden');
    }
}

export function setSavedSearchParams(savedSearchParams: Params) {
    delete savedSearchParams.SavedSearchID;
    setParams(DefaultExistingParams(savedSearchParams));
}

export function updateJobAlertFields(savedSearchParams: Params) {
    const title1 = document.querySelector<HTMLElement>('#job-alert-title');
    const title2 = document.querySelector<HTMLElement>('#job-alert-title2');
    const submitButton = document.querySelector<HTMLButtonElement>('#job-alert-submit');
    const savedSearchId = document.querySelector<HTMLInputElement>('#CandidateSavedSearchID');
    const searchName = document.querySelector<HTMLInputElement>('#SearchName');

    if (!savedSearchId || !searchName || !title2 || !title1 || !submitButton) {
        return;
    }

    if (savedSearchParams.SavedSearchID !== undefined && savedSearchParams.SavedSearchID > 0) {
        title1.textContent = editAlertTitle;
        title2.textContent = editAlertTitle;
        submitButton.textContent = editAlertButtonText;
        savedSearchId.value = savedSearchParams.SavedSearchID.toString();
        isSavedSearch = true;
    }
    else {
        title1.textContent = newAlertTitle;
        title2.textContent = newAlertTitle;
        submitButton.textContent = newAlertButtonText;
        savedSearchId.value = "0";
    }

    searchName.value = savedSearchParams.SavedSearchName ?? "";
    const selectedEmailFrequency = document.querySelector<HTMLInputElement>(`input[value="${isEmpty(savedSearchParams.EmailFrequency) ? defaultEmailFrequency : savedSearchParams.EmailFrequency}"]`);    
    if (selectedEmailFrequency) {
        selectedEmailFrequency.checked = true;
    }
}

function saveSearchToggleClick() {
    const searchName = document.querySelector<HTMLInputElement>('#SearchName');

    if (!searchName || isSavedSearch) {
        return;
    }

    searchName.value = getDefaultSearchName();
}

function getDefaultSearchName(): string {
    const searchName = document.querySelector<HTMLInputElement>('#SearchName');

    if (!searchName) {
        return "";
    }

    let name = "";
    const keyword = params.Keyword || '';
    const location = isEmpty(params.LocationName) ? '' : params.LocationName[0];
    const filterNamesAndCount = getFilterNamesAndCount();

    if (keyword !== '' && location !== '') {
        name = `${keyword} in ${location}`;
    }
    else if (keyword !== '') {
        name = keyword;
    }
    else if (location !== '') {
        name = location;
    }

    if (filterNamesAndCount[1] > 0) {
        const filterCount = filterNamesAndCount[1];

        name = name === ''
            ? `${filterNamesAndCount[0]}${filterCount > 1 ? filterCount === 2 ? ` + ${filterCount - 1} filter` : ` + ${filterCount - 1} filters` : ``}`
            : name + ` + ${filterCount} ${filterCount === 1 ? 'filter' : 'filters'}`;
    }

    return name;
}

function getSuccessMessage(frequency: string): string {
    frequency = frequency.toLowerCase();

    if (isSavedSearch) {
        return frequency == 'none'
            ? `You edited a job alert for this search. ${seeAllLink}`
            : `You edited a ${frequency} job alert for this search. ${seeAllLink}`;
    }

    return frequency == 'none'
        ? `You turned on a job alert for this search. ${seeAllLink}`
        : `You turned on a ${frequency} job alert for this search. ${seeAllLink}`;
}

function getFilterNamesAndCount(): [string, number] {
    const filtersContainer = document.querySelector<HTMLElement>('#search-filters');
    const inputNamesToExclude = ['radius', 'sort-item', 'excludeOrganizations'];
    let filterName = '';
    let count = 0;

    filtersContainer?.querySelectorAll<HTMLInputElement>('input').forEach(input => {
        if (inputNamesToExclude.includes(input.name)) {
            return;
        }
        if (input.checked) {
            filterName = getNormalizedFilterName(document.querySelector<HTMLLabelElement>(`[for="${input.id}"]`)?.innerText || '', input.id);
            count++;
        }
    });

    return [filterName, count];
}
